import { forwardRef, useCallback, useMemo, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import Drag from 'hb-builder/components/common/Drag';
import Resize from 'hb-builder/components/common/Resize';
import DynamicLayoutToolbar from 'hb-builder/components/common/Wrapper/components/DynamicLayoutToolbar';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
  useDeviceModeContext,
  useLayoutContext,
  usePanelContext,
} from 'hb-builder/hooks';
import {
  getAlignItems,
  getJustifyContent,
  getStringifiedPadding,
  shorthandParser,
} from 'hb-builder/utils/attributes';
import { getNetworkColor } from 'hb-builder/utils/colors';
import { paddingOverride } from 'hb-builder/utils/mobile';
import { VerticalSplitComponent } from 'hb-react/backoffice/components/PageBuilder/types/componentResize';

import Toolbar from './Toolbar';

const sectionsType = ['section', 'structure'];

export const NB_GRID_COLUMNS = 12;
export const COLUMN_WIDTH_ACCEPTED_VALUES =
  NB_GRID_COLUMNS &&
  new Array(NB_GRID_COLUMNS + 1)
    .fill()
    .map((value, index) => (index / NB_GRID_COLUMNS) * 100);

const getCanBeVisuallySelected = ({ type, canBeSelected }) =>
  sectionsType.includes(type) ? false : canBeSelected;

const getIsVisuallySelected = ({ isSelected, canBeVisuallySelected }) =>
  isSelected && canBeVisuallySelected;

const getDisplayLayout = ({
  isDisabled,
  isHovered,
  isSelected,
  type,
  isDisplayingToolbar,
}) => {
  const itemsWhereToDisplayOnHover = [...sectionsType, 'single'];

  return (
    isDisplayingToolbar &&
    type !== 'column' &&
    !isDisabled &&
    (isSelected || (isHovered && itemsWhereToDisplayOnHover.includes(type)))
  );
};

const getDisplayLayoutToolbar = ({
  isDisabled,
  isHovered,
  isSelected,
  itemType,
  isMobileMode,
}) =>
  !isMobileMode &&
  itemType === 'Section' &&
  !isDisabled &&
  (isSelected || isHovered);

const getDisplayResize = ({
  isHovered,
  resizedItemId,
  itemId,
  type,
  isMobileMode,
}) =>
  !isMobileMode &&
  ((type === 'column' && (resizedItemId === itemId || isHovered)) ||
    (sectionsType.includes(type) && isHovered));

const getWrapperStyle = ({ attributes, colors, isDragging, style }) => ({
  backgroundColor: getNetworkColor(
    style?.backgroundColor || attributes?.containerBackgroundColor,
    colors,
  ),
  alignItems: getAlignItems(attributes?.verticalAlign),
  justifyContent: getJustifyContent(attributes?.horizontalAlign),
  padding:
    !isDragging && attributes?.containerPadding
      ? attributes?.containerPadding
      : 0,
  width: '100%',
  ...style,
});

export const Wrapper = forwardRef(
  (
    {
      attributes = undefined,
      children,
      className = undefined,
      componentType,
      dataTestId,
      id = undefined,
      isMobilePreview,
      style = undefined,
    },
    ref,
  ) => {
    const isMobileDevice = useIsMobileDevice();
    const breakpoint = useBreakpoint();
    const theme = useTheme();

    const isMobile =
      isMobilePreview || isMobileDevice || ['s', 'm'].includes(breakpoint);

    return (
      <WrapperStyled
        className={className}
        $isDisabled
        $isMobile={isMobile}
        $paddingOverride={paddingOverride({
          keepContainerPaddingScreenS: attributes?.keepContainerPaddingScreenS,
          padding: attributes?.containerPadding,
          type: componentType,
        })}
        id={id}
        data-testid={dataTestId}
        ref={ref}
        style={getWrapperStyle({ attributes, colors: theme.colors, style })}
      >
        {children}
      </WrapperStyled>
    );
  },
);

export const WrapperBuilder = forwardRef(
  ({ attributes, children, dataTestId, id, isEmpty, style }, ref) => {
    const {
      changeColumnWidth,
      changeSectionPadding,
      getParentItem,
      isResizing,
      setSelectedItemId,
      resizedItemId,
    } = useBuilderContext();
    const {
      canBeDoubleClicked,
      canBeSelected,
      isSelected,
      item,
      label,
      nbParentChildren,
      toolbar,
      toolbarMobile,
      type,
    } = useComponentContext();
    const { isMobileMode, isDesktopMode } = useDeviceModeContext();

    const {
      hivebrite: { networkColors },
      features: { resizePadding: resizePaddingConfig },
    } = useBuilderConfigContext();
    const { isMobile, itemDragging } = useLayoutContext();
    const { displayPanel } = usePanelContext();
    const isToolbarMobileAvailable = toolbarMobile && isMobileMode;

    const toolbarItems = useMemo(() => {
      if (isToolbarMobileAvailable) {
        return toolbarMobile;
      }

      const defaultToolbar = toolbar
        ? typeof toolbar === 'function'
          ? toolbar({ item, nbParentChildren })
          : toolbar
        : [];

      return defaultToolbar;
    }, [
      isToolbarMobileAvailable,
      toolbar,
      item,
      nbParentChildren,
      toolbarMobile,
    ]);

    const [isHovered, setIsHovered] = useState(false);

    const getPositiveValue = (value) => (value < 0 ? 0 : value);

    const handleResize = (value) => {
      if (
        resizePaddingConfig?.typesSplitResizePaddingVertical?.includes(
          item.name,
        )
      ) {
        // TODO : refactor this part to avoid pointing specific component
        const itemsToUpdate =
          item.name === VerticalSplitComponent.HEROSLIDER
            ? item.children
            : [item];

        return itemsToUpdate.forEach((currentItem) => {
          const updatedPaddingTop = getPositiveValue(
            shorthandParser(currentItem.attributes.containerPadding, 'top') +
              Math.round(value / 2),
          );
          const updatedPaddingBottom = getPositiveValue(
            shorthandParser(currentItem.attributes.containerPadding, 'bottom') +
              Math.round(value / 2),
          );

          changeSectionPadding({
            item: currentItem,
            value: getStringifiedPadding({
              bottom: updatedPaddingBottom,
              left: shorthandParser(
                currentItem.attributes.containerPadding,
                'left',
              ),
              right: shorthandParser(
                currentItem.attributes.containerPadding,
                'right',
              ),
              top: updatedPaddingTop,
            }),
          });
        });
      }

      const updatedPaddingBottom = getPositiveValue(
        shorthandParser(item.attributes.containerPadding, 'bottom') +
          Math.round(value),
      );

      return changeSectionPadding({
        item,
        value: getStringifiedPadding({
          bottom: updatedPaddingBottom,
          left: shorthandParser(item.attributes.containerPadding, 'left'),
          right: shorthandParser(item.attributes.containerPadding, 'right'),
          top: shorthandParser(item.attributes.containerPadding, 'top'),
        }),
      });
    };

    const handleMouseOver = useCallback(() => {
      if (!isHovered) {
        setIsHovered(true);
      }
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
    }, []);

    const handleClick = useCallback(
      (e) => {
        e.stopPropagation();
        setSelectedItemId(item?.id);
      },
      [item],
    );

    const isDisabled =
      attributes?.disableSelection ||
      (itemDragging && itemDragging.id !== item.id);

    const renderChildren = ({ isDragging }) =>
      typeof children === 'function' ? children({ isDragging }) : children;

    const resizeProps = () => {
      if (type === 'column') {
        const initialValue = parseFloat(item?.attributes?.width);
        const sectionItem = getParentItem(item.id);

        const columnIndex = sectionItem?.children.findIndex(
          (child) => child.id === item.id,
        );
        const isFirstColumn = columnIndex === 0;
        const isLastColumn =
          columnIndex === (sectionItem?.children?.length || 0) - 1;

        return {
          initialValue,
          onChange: ({ position, value }) =>
            changeColumnWidth({
              acceptedValues: COLUMN_WIDTH_ACCEPTED_VALUES,
              item,
              nbGridColumns: NB_GRID_COLUMNS,
              oldValue: initialValue,
              position,
              value,
            }),
          positionsHandler: [
            !isFirstColumn && 'left',
            !isLastColumn && 'right',
          ].filter(Boolean),
          type: 'x',
        };
      }
      if (type === 'section') {
        return {
          initialValue: attributes?.containerPadding,
          onChange: ({ value }) => handleResize(value),
          positionsHandler: ['bottom'],
          renderLabel: (value) =>
            (Boolean(value) || value === 0) &&
            `min-height: ${parseInt(value, 10)}px`,
          type: 'y',
        };
      }

      return {};
    };

    return (
      <Drag
        component={{
          item,
          label,
          type,
        }}
      >
        {({ ref: dragRef, isDragging }) => {
          const isDisplayingToolbar = isDesktopMode || isToolbarMobileAvailable;

          // Section should be selectable (its ID should be set in the context) but not visually selected (no "selected" styles applied)
          const canBeVisuallySelected = getCanBeVisuallySelected({
            type,
            canBeSelected,
          });

          const isVisuallySelected = getIsVisuallySelected({
            isSelected,
            canBeVisuallySelected,
          });

          const displayResize = getDisplayResize({
            isHovered,
            type,
            resizedItemId,
            itemId: item.id,
            isMobileMode,
          });

          // Wether we should display the outlines around the component or not
          const displayLayout = getDisplayLayout({
            isDisabled,
            isHovered,
            isSelected: isVisuallySelected,
            type,
            isDisplayingToolbar,
          });

          // Wether we should display the settings for the columns layout or not
          const displayLayoutToolbar = getDisplayLayoutToolbar({
            isDisabled,
            isHovered,
            isSelected: isVisuallySelected,
            itemType: item.type,
            isMobileMode,
          });

          const clickHandler = (e) =>
            isDisplayingToolbar && !isDisabled && canBeSelected
              ? handleClick(e)
              : undefined;

          return (
            <BuilderThemeProvider>
              <Box
                flex={1}
                {...(isEmpty && { height: '100%' })}
                onMouseDown={clickHandler}
                onMouseLeave={!isDisabled ? handleMouseLeave : undefined}
                onMouseOver={!isDisabled ? handleMouseOver : undefined}
              >
                <WrapperStyled
                  $canBeSelected={!isDisabled && canBeVisuallySelected}
                  $displayLayout={displayLayout}
                  $isDisabled={isDisabled}
                  $isDragging={isDragging}
                  $isHovered={
                    canBeVisuallySelected && (isHovered || isDragging)
                  }
                  $isMobile={isMobile}
                  $isResizing={isResizing}
                  $isSelected={!isDisabled && isVisuallySelected}
                  $paddingOverride={paddingOverride({
                    keepContainerPaddingScreenS:
                      attributes?.keepContainerPaddingScreenS,
                    padding: attributes?.containerPadding,
                    type,
                  })}
                  $type={type}
                  data-testid={dataTestId}
                  height="100%"
                  id={id}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (canBeDoubleClicked) {
                      displayPanel?.();
                    }
                  }}
                  ref={ref}
                  style={getWrapperStyle({
                    attributes,
                    colors: networkColors,
                    isDragging,
                    style,
                  })}
                >
                  {displayLayout && (
                    <Toolbar
                      ref={dragRef}
                      isDragging={isDragging}
                      items={toolbarItems}
                      opacity={isDragging ? 0 : 1}
                    />
                  )}
                  {displayLayoutToolbar && <DynamicLayoutToolbar />}
                  {displayResize && (
                    <Resize
                      isDragging={isDragging}
                      style={{
                        ...getWrapperStyle({
                          attributes,
                          colors: networkColors,
                          isDragging,
                          style,
                        }),
                        // needed so background color don't cumulate when transparent
                        backgroundColor: 'transparent',
                        backgroundImage: 'none',
                      }}
                      {...resizeProps()}
                    />
                  )}
                  <BuilderThemeProvider viewAs={VIEW_AS.USER}>
                    {renderChildren({ isDragging })}
                  </BuilderThemeProvider>
                </WrapperStyled>
              </Box>
            </BuilderThemeProvider>
          );
        }}
      </Drag>
    );
  },
);

const getBorderStyle = ({ theme, $type, $isDragging }) => {
  const borderStyle = $isDragging ? 'none' : `1px dashed ${theme.colors.dove}`;

  return $type === 'section'
    ? `
        border-top: ${borderStyle};
        border-bottom: ${borderStyle};
      `
    : `
        border: 1px solid ${theme.colors.primary};
        outline: 1px solid ${theme.colors.primary};
        border-radius: 2px;
      `;
};

const getLayout = ({ $type }) => css`
  &:after {
    ${getBorderStyle}
    bottom: ${$type === 'single' ? 0 : '-1px'};
    content: ' ';
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    z-index: ${$type === 'single' ? 300 : 200};
  }
`;

const getMobileStyle = ({ $paddingOverride }) =>
  css`
    iframe {
      height: 100% !important;
      width: 100% !important;
    }

    ${$paddingOverride && `padding: ${$paddingOverride} !important`};
  `;

const WrapperStyled = styled(Box).attrs(() => ({
  width: '100%',
}))`
  ${({
    $canBeSelected,
    $displayLayout,
    $isDisabled,
    $isDragging,
    $isMobile,
    $isSelected,
    theme,
    $type,
  }) => {
    const pointerEvents =
      sectionsType.includes($type) || $canBeSelected || $isDisabled
        ? 'all'
        : 'none';
    const cursor =
      sectionsType.includes($type) || $isSelected || $isDisabled
        ? 'unset'
        : 'pointer';

    return css`
      cursor: ${cursor};
      margin: 0 auto;
      opacity: ${$isDragging ? 0.6 : undefined};
      pointer-events: ${pointerEvents};
      position: relative;
      z-index: ${$isDragging ? 1 : undefined};

      ${$displayLayout && getLayout({ theme, $type, $isDragging })}

      ${$isMobile && getMobileStyle}
    `;
  }}
`;
